/**
 * SADARayon — Conflits de planning (Phase 1).
 *
 * Affiche dans le Pilotage :
 *   - rayons avec ≥ 2 inventaires actifs en parallèle (doublons opérationnels)
 *   - plannings programmés bloqués par un inventaire précédent non clôturé
 *
 * Source : GET /api/inventory/planning-conflicts (admin/manager)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertOctagon, CalendarClock, RefreshCw, AlertTriangle, Eraser, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
};

const STATUS_TONES = {
  a_faire: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  en_cours: 'bg-blue-100 text-blue-700 border-blue-200',
  en_cours_op1: 'bg-blue-100 text-blue-700 border-blue-200',
  cloture_op1: 'bg-amber-100 text-amber-800 border-amber-200',
  en_cours_op2: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  cloture_op2: 'bg-purple-100 text-purple-700 border-purple-200',
  en_retard: 'bg-red-100 text-red-700 border-red-200',
};

const InventoryConflictsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/planning-conflicts`, { headers: auth() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement conflits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCleanup = async () => {
    setCleanupLoading(true);
    try {
      // 1. Dry-run pour récupérer le nombre d'orphelins
      const dryRes = await axios.post(
        `${API}/inventory/cleanup-orphan-inventories?dry_run=true`,
        {},
        { headers: auth() },
      );
      const { orphans_found = 0, protected_with_data = [] } = dryRes.data || {};
      if (orphans_found === 0) {
        toast.info(`Aucun retard fantôme à purger. ${protected_with_data.length} inventaire(s) avec saisies préservé(s).`);
        return;
      }
      if (!window.confirm(
        `Purger ${orphans_found} inventaire(s) actif(s) sans planning associé ?\n\n`
        + `${protected_with_data.length} inventaire(s) avec saisies seront PRÉSERVÉS.\n\n`
        + 'Action réversible côté base (soft-delete avec raison "orphan_cleanup").',
      )) return;
      const res = await axios.post(
        `${API}/inventory/cleanup-orphan-inventories?dry_run=false`,
        {},
        { headers: auth() },
      );
      toast.success(
        `${res.data?.orphans_deleted || 0} retard(s) fantôme(s) purgé(s). Pilotage à jour.`,
        { duration: 6000 },
      );
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  const onAutoFixConflicts = async () => {
    setCleanupLoading(true);
    try {
      const dry = await axios.post(
        `${API}/inventory/plannings/auto-fix-conflicts?dry_run=true`,
        {}, { headers: auth() },
      );
      const { fixed = 0, items = [], responsable_conflicts = [], warnings = [] } = dry.data || {};
      if (fixed === 0 && responsable_conflicts.length === 0) {
        toast.info('Aucun conflit d\'affectation détecté.');
        return;
      }
      const sample = items.slice(0, 5).map((it) => {
        const op1 = it.op1_name_to ? `OP1: ${it.op1_name_from}→${it.op1_name_to}` : '';
        const op2 = it.op2_name_to ? `OP2: ${it.op2_name_from}→${it.op2_name_to}` : '';
        return `• ${it.date} ${it.zone_name} (${[op1, op2].filter(Boolean).join(' · ')})`;
      }).join('\n');
      const respMsg = responsable_conflicts.length > 0
        ? `\n\n⚠️ ${responsable_conflicts.length} responsable(s) en conflit non corrigé(s) automatiquement (à corriger manuellement) :\n`
          + responsable_conflicts.slice(0, 3).map((r) => `• ${r.date} ${r.zone_name} : ${r.responsable_name}`).join('\n')
        : '';
      const warnMsg = warnings.length > 0
        ? `\n\n⚠️ ${warnings.length} avertissement(s) — voir console après exécution.`
        : '';
      if (!window.confirm(
        `Corriger automatiquement ${fixed} planning(s) (OP1/OP2) avec agents en conflit (manager, congé, jour de repos) ?\n\n`
        + sample + (items.length > 5 ? `\n…et ${items.length - 5} de plus` : '')
        + respMsg + warnMsg
        + `\n\nAction réversible côté base.`,
      )) return;
      const res = await axios.post(
        `${API}/inventory/plannings/auto-fix-conflicts?dry_run=false`,
        {}, { headers: auth() },
      );
      toast.success(
        `${res.data?.fixed || 0} planning(s) corrigé(s) automatiquement.`
        + (res.data?.responsable_conflicts?.length ? ` ${res.data.responsable_conflicts.length} responsable(s) à revoir manuellement.` : ''),
        { duration: 8000 },
      );
      if ((res.data?.warnings || []).length > 0) {
        console.warn('[auto-fix] warnings:', res.data.warnings);
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur correction conflits');
    } finally {
      setCleanupLoading(false);
    }
  };

  const onForceCleanup = async () => {
    setCleanupLoading(true);
    try {
      // 1. Dry-run avec force=true pour lister TOUS les orphelins (saisies incluses)
      const dryRes = await axios.post(
        `${API}/inventory/cleanup-orphan-inventories?dry_run=true&force=true`,
        {},
        { headers: auth() },
      );
      const { orphans = [] } = dryRes.data || {};
      const withData = orphans.filter((o) => (o.lines_saisies || 0) > 0);
      if (orphans.length === 0) {
        toast.info('Aucun inventaire orphelin à forcer.');
        return;
      }
      const summary = orphans
        .slice(0, 10)
        .map((o) => `• ${o.zone_name || '—'} (${o.status}, ${o.lines_saisies || 0} ligne(s) saisie(s))`)
        .join('\n');
      const extra = orphans.length > 10 ? `\n…et ${orphans.length - 10} de plus` : '';
      if (!window.confirm(
        `⚠️ FORCER la purge de ${orphans.length} inventaire(s) orphelin(s) — dont ${withData.length} AVEC saisies ?\n\n`
        + `${summary}${extra}\n\n`
        + 'Toutes les lignes saisies seront SUPPRIMÉES. '
        + 'Action réversible uniquement côté base (soft-delete).',
      )) return;
      // 2. Double confirmation pour les inventaires avec saisies
      if (withData.length > 0 && !window.confirm(
        `Confirmer DÉFINITIVEMENT la suppression de ${withData.length} inventaire(s) avec saisies OP1/OP2 ?`,
      )) return;
      const res = await axios.post(
        `${API}/inventory/cleanup-orphan-inventories?dry_run=false&force=true`,
        {},
        { headers: auth() },
      );
      toast.success(
        `${res.data?.orphans_deleted || 0} inventaire(s) purgé(s) (forcé). Pilotage à jour.`,
        { duration: 6000 },
      );
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur cleanup forcé');
    } finally {
      setCleanupLoading(false);
    }
  };

  const duplicates = data?.duplicate_active_inventories || [];
  const conflicts = data?.pending_planning_conflicts || [];
  const hasIssues = duplicates.length > 0 || conflicts.length > 0;

  return (
    <div
      className={`rounded-lg border overflow-hidden ${hasIssues ? 'border-red-200 bg-red-50/30' : 'border-zinc-200 bg-white'}`}
      data-testid="pilotage-conflicts"
    >
      <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2 border-inherit">
        <div className="flex items-center gap-2">
          {hasIssues
            ? <AlertOctagon className="w-4 h-4 text-red-600" />
            : <CalendarClock className="w-4 h-4 text-emerald-600" />}
          <h3 className="font-medium text-zinc-900">
            Conflits de planning
            {hasIssues && (
              <span className="text-red-600"> · {duplicates.length + conflicts.length} alerte(s)</span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={onAutoFixConflicts}
            disabled={cleanupLoading || loading}
            data-testid="pilotage-conflicts-auto-fix"
            className="text-amber-800 border-amber-400 hover:bg-amber-50 font-medium"
            title="Détecte tous les agents en congé, jour de repos, manager exclu sur les plannings actifs et les remplace automatiquement"
          >
            {cleanupLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />}
            Corriger conflits d&apos;affectation
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCleanup}
            disabled={cleanupLoading || loading}
            data-testid="pilotage-conflicts-cleanup-orphans"
            className="text-red-700 border-red-300 hover:bg-red-50"
            title="Supprime les inventaires actifs sans planning (retards fantômes) — saisies préservées"
          >
            {cleanupLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              : <Eraser className="w-3.5 h-3.5 mr-1.5" />}
            Purger les retards fantômes
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onForceCleanup}
            disabled={cleanupLoading || loading}
            data-testid="pilotage-conflicts-force-cleanup"
            className="text-red-900 border-red-500 hover:bg-red-100 bg-red-50/40"
            title="FORCE la purge des inventaires orphelins, INCLUS ceux avec saisies OP1/OP2"
          >
            {cleanupLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />}
            Forcer la purge (saisies incluses)
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={reload}
            disabled={loading}
            data-testid="pilotage-conflicts-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!hasIssues && !loading && (
        <div className="px-4 py-5 text-center text-sm text-emerald-700" data-testid="pilotage-conflicts-empty">
          ✓ Aucun doublon ni planning bloqué. {data && (
            <span className="text-zinc-500">
              · {data.total_active_inventories} inventaire(s) actif(s) sur {data.zones_with_active} rayon(s)
            </span>
          )}
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="px-4 py-3 border-b border-red-200">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-red-800">
            <AlertTriangle className="w-4 h-4" />
            Rayons avec plusieurs inventaires actifs en parallèle
            <Badge className="bg-red-100 text-red-800 border-red-200 border ml-1">{duplicates.length}</Badge>
          </div>
          <ul className="space-y-2.5">
            {duplicates.map((d) => (
              <li
                key={d.zone_id}
                className="rounded-md border border-red-200 bg-white p-3"
                data-testid={`pilotage-duplicate-${d.zone_id}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-bold text-zinc-900">{d.zone_name}</span>
                  <Badge className="bg-red-100 text-red-800 border-red-200 border">
                    {d.count} inventaires
                  </Badge>
                </div>
                <ul className="text-xs space-y-0.5 mt-1.5 ml-3">
                  {d.inventories.map((inv) => (
                    <li key={inv.inventory_id} className="flex items-center gap-2">
                      <Badge className={`${STATUS_TONES[inv.status] || STATUS_TONES.a_faire} border text-[10px]`}>
                        {inv.status}
                      </Badge>
                      <span className="font-mono text-zinc-400">{inv.inventory_id.slice(0, 8)}</span>
                      <span className="text-zinc-600">démarré {fmtDate(inv.date_started)}</span>
                      <span className="text-zinc-700">· OP1 {inv.op1_name || '—'}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 mt-2 italic">
                  💡 Clôturez les inventaires en doublon ou supprimez les superflus pour libérer le rayon.
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-amber-800">
            <CalendarClock className="w-4 h-4" />
            Plannings bloqués par un inventaire non clôturé
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 border ml-1">{conflicts.length}</Badge>
          </div>
          <ul className="space-y-1.5">
            {conflicts.map((c) => (
              <li
                key={c.planning_id}
                className="text-sm flex items-start gap-2 p-2 rounded-md hover:bg-zinc-50"
                data-testid={`pilotage-conflict-${c.planning_id}`}
              >
                <span className="font-mono text-xs text-zinc-500 mt-0.5 whitespace-nowrap">
                  {fmtDate(c.scheduled_date)}
                </span>
                <span className="font-medium text-zinc-900">{c.zone_name}</span>
                {c.is_overdue && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 border text-[10px]">
                    En retard
                  </Badge>
                )}
                <span className="text-zinc-500">→ bloqué par inventaire</span>
                <Badge className={`${STATUS_TONES[c.blocking_inventory.status] || STATUS_TONES.a_faire} border text-[10px]`}>
                  {c.blocking_inventory.status}
                </Badge>
                <span className="text-zinc-400 text-xs italic">depuis {fmtDate(c.blocking_inventory.date_started)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InventoryConflictsSection;
